/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMap } from 'react-leaflet'
import L from 'leaflet'
import { useTranslation } from 'react-i18next'
import { Icon } from 'semantic-ui-react'

const styles = {
    header: css`
        font-weight: 600;
        font-size: 12px;
        color: #555;
        margin-bottom: 6px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    `,
    row: css`
        display: flex;
        align-items: stretch;
        gap: 4px;
        padding: 3px 0;
    `,
    clickableRow: css`
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 3px 0;
        cursor: pointer;
        user-select: none;
    `,
    platformToggle: css`
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        flex: 1;
        user-select: none;
    `,
    platformImg: css`
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
        object-fit: cover;
        flex-shrink: 0;
    `,
    label: css`
        font-size: 13px;
        color: #333;
        flex: 1;
    `,
    iconBox: css`
        width: 24px;
        height: 24px;
        display: flex;
        align-items: stretch;
        justify-content: center;
        flex-shrink: 0;
    `,
    endIcon: css`
        &.icon {
            margin: 0 !important;
            margin-bottom: .5rem !important;
            font-size: 16px !important;
        }
    `,
    polygonIcon: css`
        &.icon {
            margin: 0 !important;
            font-size: 16px !important;
            cursor: pointer;
        }
    `,
}

const LegendControl = ({
                           platforms,
                           polygons,
                           hasHeatmap,
                           hiddenPlatformModules,
                           visiblePolygonKeys,
                           hiddenOverlays,
                           onTogglePlatform,
                           onTogglePolygon,
                           onToggleOverlay,
                       }) => {
    const map = useMap()
    const [container, setContainer] = useState(null)
    const [t] = useTranslation()

    useEffect(() => {
        const ControlClass = L.Control.extend({
            onAdd() {
                const div = L.DomUtil.create('div', 'leaflet-control leaflet-bar')
                div.style.background = 'white'
                div.style.borderRadius = '4px'
                div.style.boxShadow = '0 1px 5px rgba(0,0,0,0.4)'
                div.style.padding = '8px 10px'
                div.style.minWidth = '160px'
                L.DomEvent.disableClickPropagation(div)
                L.DomEvent.disableScrollPropagation(div)
                setContainer(div)
                return div
            },
            onRemove() {
                setContainer(null)
            },
        })
        const control = new ControlClass({position: 'bottomleft'})
        control.addTo(map)
        return () => { control.remove() }
    }, [map])

    if (!container) return null

    const allPlatformsPolygon = polygons?.find(p => p.platformModule === null)
    const hasAnyContent = (platforms && platforms.length > 0) || allPlatformsPolygon || hasHeatmap

    container.style.display = hasAnyContent ? '' : 'none'
    if (!hasAnyContent) return null

    return createPortal(
        <div>
            <div css={styles.header}>Légende</div>

            {platforms?.map((module) => {
                const pointsActive = !hiddenPlatformModules.has(module)
                const platformPolygon = polygons?.find(p => p.platformModule === module)
                const polygonActive = platformPolygon && visiblePolygonKeys.has(platformPolygon.key)

                return (
                    <div key={module} css={styles.row}>
                        <div
                            css={[styles.platformToggle, css`opacity: ${pointsActive ? 1 : 0.4};`]}
                            onClick={() => onTogglePlatform(module)}
                        >
                            <img
                                src={`/img/platforms/${module}.jpg`}
                                alt={module}
                                css={styles.platformImg}
                            />
                            <span css={styles.label}>
                                {t(`catchment_area.platforms.${module}`, {defaultValue: module})}
                            </span>
                        </div>

                        {platformPolygon && (
                            <Icon
                                fitted
                                name={polygonActive ? 'map outline' : 'map'}
                                css={[styles.polygonIcon, css`opacity: ${polygonActive ? 1 : 0.35};`]}
                                onClick={() => onTogglePolygon(platformPolygon.key)}
                                title="Afficher/masquer la zone"
                            />
                        )}
                    </div>
                )
            })}

            {allPlatformsPolygon && (
                <div
                    css={[styles.clickableRow, css`opacity: ${visiblePolygonKeys.has(allPlatformsPolygon.key) ? 1 : 0.4};`]}
                    onClick={() => onTogglePolygon(allPlatformsPolygon.key)}
                >
                    <div css={styles.iconBox}>
                        <Icon name="globe" css={styles.endIcon}/>
                    </div>
                    <span css={styles.label}>
                        {t('catchment_area.polygons.all', {defaultValue: t('catchment_area.platforms.all_platforms')})}
                    </span>
                    <Icon
                        name={visiblePolygonKeys.has(allPlatformsPolygon.key) ? 'map outline' : 'map'}
                        css={styles.endIcon}
                    />
                </div>
            )}

            {hasHeatmap && (
                <div
                    css={[styles.clickableRow, css`opacity: ${!hiddenOverlays.has('heatmap') ? 1 : 0.4};`]}
                    onClick={() => onToggleOverlay('heatmap')}
                >
                    <div css={styles.iconBox}>
                        <Icon name="fire" css={styles.endIcon}/>
                    </div>
                    <span css={styles.label}>
                        {t('catchment_area.overlays.heatmap', {defaultValue: 'Heatmap'})}
                    </span>
                    <Icon
                        name={!hiddenOverlays.has('heatmap') ? 'eye' : 'eye slash'}
                        css={styles.endIcon}
                    />
                </div>
            )}
        </div>,
        container,
    )
}

export { LegendControl }
